import _ from 'lodash';
import React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import { faAngleLeft, faAngleRight, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dateStringAsKey, localizedDateFormat, localizedToday } from './atAGlanceTabUtil';
import RevenueUpliftSelect from './RevenueUpliftSelect';
import PerformanceBarChart from './charts/PerformanceBarChart';
import PerformanceLineChart, { PERFORMANCE_LINE_CHART_COLORS } from './charts/PerformanceLineChart';
import PerformanceBreakdownPlot from './charts/PerformanceBreakdownPlot';
import { PerformanceToolbar } from './FiltersToolbar';
import PerformanceTabContext from './performanceTabContext';
import {
  GRAPH_TIME_GRANULARITY_LABELS,
  METRIC_OPTIONS,
  MONTH_NAMES,
  PERFORMANCE_SETTINGS_KEYS,
  PERIOD_OPTIONS,
  PERIOD_TO_FORMAT_TYPE,
  PeriodType,
  SALES_QUANTITY,
  WEEKDAY_NAMES,
} from './performanceConstants';
import { getLineGraphLegend } from './performanceTabUtil';
import { alignedDateInRange, getRanges, MONTH, PERIOD_SCOPE, QUARTER, utcDate, WEEK, YEAR } from './relativeDates';
import { DashboardContext, DomainContext } from './revenueUpliftContexts';
import usePerformanceData from './usePerformanceData';
import usePerformanceBreakdownData from './usePerformanceBreakdownData';
import { ProfitRoverCard } from '../../../generic/ProfitRoverCard';
import ProfitRoverSkeleton from '../../../skeleton/ProfitRoverSkeleton';
import { CenteredProfitRoverSpinner } from '../../../spinner/ProfitRoverSpinner';
import { DateType, compactFormatNumber, convertTo12HourFormat, shortRange } from '../../../util/format';
import { HOUR_DIMENSION_TYPE } from '../../../workflow/workflowConstants';
import { usePerformanceForecastMeta } from '../../../../data-access/query/priceStats';
import './performance-tab.scss';

const formatBarGraphTitle = (selectedPeriod, daySelected, cumulativeTotals, timezone, locale) => {
  const formattedDate = localizedDateFormat(daySelected, timezone, locale, PERIOD_TO_FORMAT_TYPE[selectedPeriod.value]);

  return cumulativeTotals ? `Thru ${formattedDate}` : formattedDate;
};

const DisplayOptions = {
  REVENUE: 'revenue',
  PERCENTAGE: 'percentage',
};

const mapDimensionIdsToNames = (dataList, referenceList, is12HourFormat) => {
  let idToLabelMap;

  if (is12HourFormat) {
    idToLabelMap = new Map(referenceList.map(item => [String(item.id), convertTo12HourFormat(item.value)]));
  } else {
    idToLabelMap = new Map(referenceList.map(item => [String(item.id), item.value]));
  }

  return dataList.map(({ name, ...rest }) => ({
    name: idToLabelMap.get(name) || name,
    ...rest,
  }));
};

const ResizeContainer = ({ children, className }) => {
  const { ref, height, width } = useResizeDetector();

  return (
    <div ref={ref} className={className}>
      {children({ height, width })}
    </div>
  );
};

const BreakdownSection = ({
  selectedPeriod,
  selectedMetric,
  cumulativeTotals,
  periodScope,
  daySelected,
  graphNumberFormatter,
  dateFormatter,
  barGraphData,
  openFiltersMenu,
}) => {
  const { dimensions, dimensionValues, is24HourFormat, locationTimezone } = React.useContext(DomainContext);
  const { currencySymbol, relativeDates, setFocusedDimensionId, performanceSettingsApi } = React.useContext(
    DashboardContext,
  );
  const { currentPerformanceSettings, saveCurrentPerformanceSettings } = performanceSettingsApi;
  const savedDimensionId = currentPerformanceSettings[PERFORMANCE_SETTINGS_KEYS.BREAKDOWN_BY_DIMENSION_ID];
  const savedPeriodId = currentPerformanceSettings[PERFORMANCE_SETTINGS_KEYS.BREAKDOWN_PERIOD];

  const rangesApi = React.useMemo(() => getRanges(selectedPeriod.value, relativeDates, periodScope), [
    selectedPeriod.value,
    relativeDates,
    periodScope,
  ]);
  const { granularity, currentRange, lastRange, stlyRange } = rangesApi;

  const hourDimId = React.useMemo(
    () => dimensions?.find(dim => dim.dimension_type === HOUR_DIMENSION_TYPE)?.product_dimension_id,
    [dimensions],
  );
  const dimensionsOptions = React.useMemo(
    () => dimensions.map(dimension => ({ value: dimension.product_dimension_id, label: dimension.name })),
    [dimensions],
  );
  const defaultDimensionOption = savedDimensionId
    ? {
        value: savedDimensionId,
        label: dimensionsOptions.find(dim => dim.value === savedDimensionId)?.label,
      }
    : dimensionsOptions[0];

  const timePeriodOptions = React.useMemo(() => {
    const options = [
      {
        value: PeriodType.CURRENT,
        label: getLineGraphLegend(PeriodType.CURRENT, selectedPeriod.value, rangesApi, dateFormatter),
      },
      {
        value: PeriodType.LAST,
        label: getLineGraphLegend(PeriodType.LAST, selectedPeriod.value, rangesApi, dateFormatter),
      },
    ];

    if (stlyRange) {
      options.push({
        value: PeriodType.STLY,
        label: getLineGraphLegend(PeriodType.STLY, selectedPeriod.value, rangesApi, dateFormatter),
      });
    }

    return options;
  }, [dateFormatter, rangesApi, selectedPeriod.value, stlyRange]);

  const defaultTimePeriodOption = React.useMemo(() => {
    if (savedPeriodId === PeriodType.STLY && selectedPeriod.value === YEAR) {
      return timePeriodOptions[1];
    }
    if (savedPeriodId) {
      return {
        value: savedPeriodId,
        label: timePeriodOptions.find(opt => opt.value === savedPeriodId)?.label,
      };
    }
    return timePeriodOptions[0];
  }, [savedPeriodId, selectedPeriod.value, timePeriodOptions]);

  const [dimensionSelected, setDimensionSelected] = React.useState(defaultDimensionOption);
  const [timePeriodSelected, setTimePeriodSelected] = React.useState(defaultTimePeriodOption);

  const deferredDimId = React.useDeferredValue(dimensionSelected.value);
  const deferredPeriod = React.useDeferredValue(timePeriodSelected.value);
  const isCalculatingResults = deferredDimId !== dimensionSelected.value || deferredPeriod !== timePeriodSelected.value;

  const [breakdownValuesDisplay, setBreakdownValuesDisplay] = React.useState(
    selectedMetric.value !== SALES_QUANTITY ? DisplayOptions.REVENUE : DisplayOptions.PERCENTAGE,
  );

  const onDimensionChange = dim => {
    saveCurrentPerformanceSettings({
      ...currentPerformanceSettings,
      [PERFORMANCE_SETTINGS_KEYS.BREAKDOWN_BY_DIMENSION_ID]: dim.value,
    });
    setDimensionSelected(dim);
  };
  const onTimePeriodChange = period => {
    saveCurrentPerformanceSettings({
      ...currentPerformanceSettings,
      [PERFORMANCE_SETTINGS_KEYS.BREAKDOWN_PERIOD]: period.value,
    });
    setTimePeriodSelected(period);
  };

  const lastAlignedDate = React.useMemo(() => alignedDateInRange(daySelected, currentRange, lastRange, granularity), [
    daySelected,
    currentRange,
    lastRange,
    granularity,
  ]);

  const stlyAlignedDate = React.useMemo(() => alignedDateInRange(daySelected, currentRange, stlyRange, granularity), [
    daySelected,
    currentRange,
    stlyRange,
    granularity,
  ]);

  const dateKey = React.useMemo(() => dateStringAsKey(daySelected, locationTimezone, granularity), [
    daySelected,
    locationTimezone,
    granularity,
  ]);
  const lastKey = React.useMemo(
    () => (lastAlignedDate ? dateStringAsKey(lastAlignedDate, locationTimezone, granularity) : null),
    [lastAlignedDate, locationTimezone, granularity],
  );
  const stlyKey = React.useMemo(
    () => (stlyAlignedDate ? dateStringAsKey(stlyAlignedDate, locationTimezone, granularity) : null),
    [stlyAlignedDate, locationTimezone, granularity],
  );

  const productDimValuesById = React.useMemo(() => _.groupBy(dimensionValues, 'dimension_id'), [dimensionValues]);
  const dimensionSelectedValues = React.useMemo(() => {
    return productDimValuesById[deferredDimId] || [];
  }, [deferredDimId, productDimValuesById]);

  const is12HourFormat = React.useMemo(() => {
    return (
      !is24HourFormat && dimensionSelectedValues.length > 0 && dimensionSelectedValues[0].dimension_id === hourDimId
    );
  }, [dimensionSelectedValues, hourDimId, is24HourFormat]);

  const { currentTopBreakdown, lastTopBreakdown, stlyTopBreakdown } = usePerformanceBreakdownData(
    selectedPeriod.value,
    selectedMetric.value,
    deferredDimId,
    relativeDates,
    periodScope,
    cumulativeTotals,
    graphNumberFormatter,
  );

  const currentTopBreakdownData = React.useMemo(
    () => mapDimensionIdsToNames(currentTopBreakdown[dateKey] || [], dimensionSelectedValues, is12HourFormat),
    [currentTopBreakdown, dateKey, dimensionSelectedValues, is12HourFormat],
  );
  const lastTopBreakdownData = React.useMemo(
    () =>
      lastKey ? mapDimensionIdsToNames(lastTopBreakdown[lastKey] || [], dimensionSelectedValues, is12HourFormat) : [],
    [lastKey, lastTopBreakdown, dimensionSelectedValues, is12HourFormat],
  );
  const stlyTopBreakdownData = React.useMemo(
    () =>
      stlyKey ? mapDimensionIdsToNames(stlyTopBreakdown[stlyKey] || [], dimensionSelectedValues, is12HourFormat) : [],
    [stlyKey, stlyTopBreakdown, dimensionSelectedValues, is12HourFormat],
  );

  const breakdownPlotColor = React.useMemo(() => {
    switch (deferredPeriod) {
      case PeriodType.CURRENT:
        return PERFORMANCE_LINE_CHART_COLORS[0];
      case PeriodType.LAST:
        return PERFORMANCE_LINE_CHART_COLORS[1];
      case PeriodType.STLY:
        return PERFORMANCE_LINE_CHART_COLORS[2];
      default:
        return PERFORMANCE_LINE_CHART_COLORS[0];
    }
  }, [deferredPeriod]);

  const breakdownPlotData = React.useMemo(() => {
    switch (deferredPeriod) {
      case PeriodType.CURRENT:
        return currentTopBreakdownData;
      case PeriodType.LAST:
        return lastTopBreakdownData;
      case PeriodType.STLY:
        return stlyTopBreakdownData;
      default:
        return currentTopBreakdownData;
    }
  }, [currentTopBreakdownData, lastTopBreakdownData, stlyTopBreakdownData, deferredPeriod]);

  const onFilterClick = () => {
    setFocusedDimensionId(deferredDimId);
    openFiltersMenu();
  };

  const handleBreakdownValuesDisplay = (event, displayValue) => {
    setBreakdownValuesDisplay(displayValue);
  };

  return (
    <div className="breakdown-section">
      <div>
        <h6 className="title">Total</h6>
        <ResizeContainer className="breakdown-bar-chart">
          {({ height, width }) => (
            <PerformanceBarChart
              data={barGraphData}
              height={height}
              width={width}
              numberFormatter={graphNumberFormatter}
            />
          )}
        </ResizeContainer>
      </div>
      <h6 className="title mt-2">Breakdown</h6>
      <div className="view-top-by">
        <span className="label">By:</span>
        <div style={{ minWidth: '110px', maxWidth: '150px' }}>
          <RevenueUpliftSelect value={dimensionSelected} options={dimensionsOptions} onChange={onDimensionChange} />
        </div>
        <span className="label">Period:</span>
        <div style={{ minWidth: '140px', maxWidth: '200px' }}>
          <RevenueUpliftSelect value={timePeriodSelected} options={timePeriodOptions} onChange={onTimePeriodChange} />
        </div>
        <ToggleButtonGroup
          value={breakdownValuesDisplay}
          color="primary"
          size="small"
          exclusive
          onChange={handleBreakdownValuesDisplay}
          sx={{
            '& .MuiToggleButton-root': {
              minWidth: 26,
              height: 26,
            },
            '& .MuiSvgIcon-root': { fontSize: 16 },
          }}
        >
          <ToggleButton value={DisplayOptions.REVENUE} disabled={selectedMetric.value === SALES_QUANTITY}>
            <div style={{ paddingLeft: '4px', paddingRight: '4px' }}>{currencySymbol}</div>
          </ToggleButton>
          <ToggleButton value={DisplayOptions.PERCENTAGE}>
            <PercentIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <button type="button" className="open-button mb-1" onClick={onFilterClick}>
          <FontAwesomeIcon icon={faFilter} style={{ fontSize: '1rem' }} />
        </button>
      </div>

      {isCalculatingResults ? (
        <div style={{ display: 'flex', flex: 1 }}>
          <CenteredProfitRoverSpinner />
        </div>
      ) : (
        <ResizeContainer className="breakdown-plot">
          {({ height, width }) => (
            <PerformanceBreakdownPlot
              data={breakdownPlotData}
              height={height}
              width={width}
              numberFormatter={graphNumberFormatter}
              color={breakdownPlotColor}
              showAsPercentages={breakdownValuesDisplay === DisplayOptions.PERCENTAGE}
            />
          )}
        </ResizeContainer>
      )}
    </div>
  );
};

const ScopeControl = ({ title, currentScope, onPreviousScope, onNextScope }) => (
  <div className="date-control">
    <div className="container">
      <button
        type="button"
        className="button"
        onClick={onPreviousScope}
        disabled={currentScope === PERIOD_SCOPE.PREVIOUS}
      >
        <FontAwesomeIcon icon={faAngleLeft} />
      </button>
      <h2 className="title">{title}</h2>
      <button type="button" className="button" onClick={onNextScope} disabled={currentScope === PERIOD_SCOPE.CURRENT}>
        <FontAwesomeIcon icon={faAngleRight} />
      </button>
    </div>
  </div>
);

const PerformanceTab = ({ openFiltersMenu }) => {
  const { currencySymbol, locale, relativeDates, performanceSettingsApi } = React.useContext(DashboardContext);
  const { locationTimezone } = React.useContext(DomainContext);

  const { currentPerformanceSettings, saveCurrentPerformanceSettings } = performanceSettingsApi;

  const savedPeriod = currentPerformanceSettings[PERFORMANCE_SETTINGS_KEYS.PERIOD];
  const savedMetric = currentPerformanceSettings[PERFORMANCE_SETTINGS_KEYS.METRIC];
  const savedCumulative = currentPerformanceSettings[PERFORMANCE_SETTINGS_KEYS.CUMULATIVE] ?? true;
  const defaultPeriod = savedPeriod
    ? {
        value: savedPeriod,
        label: PERIOD_OPTIONS.find(option => option.value === savedPeriod)?.label,
      }
    : PERIOD_OPTIONS[1];
  const defaultMetric = savedMetric
    ? {
        value: savedMetric,
        label: METRIC_OPTIONS.find(option => option.value === savedMetric)?.label,
      }
    : METRIC_OPTIONS[0];

  const [selectedPeriod, setSelectedPeriod] = React.useState(defaultPeriod);
  const [selectedMetric, setSelectedMetric] = React.useState(defaultMetric);
  const [cumulativeTotals, setCumulativeTotals] = React.useState(savedCumulative);

  const [daySelected, setDaySelected] = React.useState(localizedToday(locationTimezone));
  const [periodScope, setPeriodScope] = React.useState(PERIOD_SCOPE.CURRENT);

  const [isLoadingGraphs, startGraphTransition] = React.useTransition();
  const deferredPeriod = React.useDeferredValue(selectedPeriod.value);
  const deferredMetric = React.useDeferredValue(selectedMetric.value);
  const deferredCumulative = React.useDeferredValue(cumulativeTotals);
  const isCalculatingResults =
    deferredPeriod !== selectedPeriod.value ||
    deferredMetric !== selectedMetric.value ||
    deferredCumulative !== cumulativeTotals;

  const onPeriodChange = period => {
    saveCurrentPerformanceSettings({ ...currentPerformanceSettings, [PERFORMANCE_SETTINGS_KEYS.PERIOD]: period.value });
    setSelectedPeriod(period);
  };
  const onMetricChange = metric => {
    saveCurrentPerformanceSettings({ ...currentPerformanceSettings, [PERFORMANCE_SETTINGS_KEYS.METRIC]: metric.value });
    setSelectedMetric(metric);
  };
  const onCumulativeToggle = checked => {
    saveCurrentPerformanceSettings({ ...currentPerformanceSettings, [PERFORMANCE_SETTINGS_KEYS.CUMULATIVE]: checked });
    setCumulativeTotals(checked);
  };

  const rangesApi = React.useMemo(() => getRanges(selectedPeriod.value, relativeDates, periodScope), [
    periodScope,
    relativeDates,
    selectedPeriod.value,
  ]);

  React.useEffect(() => {
    // reset day selected when period/metric/cumulative changes (bar graph)
    const { currentRange } = rangesApi;
    if (currentRange) {
      setDaySelected(currentRange.end);
    }
  }, [cumulativeTotals, rangesApi, selectedPeriod.value, selectedMetric.value, periodScope]);

  const graphNumberFormatter = React.useCallback(
    value =>
      compactFormatNumber(value, {
        formatAsCurrency: selectedMetric.value !== SALES_QUANTITY,
        currencySymbol,
        fixedDecimalDigits: 1,
      }),
    [currencySymbol, selectedMetric.value],
  );

  const dateFormatter = React.useCallback(
    (date, { endDate, type } = {}) => {
      let formattedDate;

      if (endDate) {
        return shortRange(date, endDate, locale);
      }

      switch (type) {
        case DateType.DAY:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, 'month-day');
          break;
        case DateType.DAY_YEAR:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, 'full');
          break;
        case DateType.MONTH:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, 'month-year');
          break;
        case DateType.QUARTER:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, 'month-year');
          break;
        case DateType.YEAR:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, YEAR);
          break;
        default:
          formattedDate = localizedDateFormat(date, locationTimezone, locale, 'month-day');
      }
      return formattedDate;
    },
    [locale, locationTimezone],
  );

  const formatTitle = React.useCallback(() => {
    const { currentRange } = rangesApi;

    switch (selectedPeriod.value) {
      case WEEK: {
        return dateFormatter(currentRange.start, { endDate: currentRange.end });
      }
      case MONTH: {
        return dateFormatter(currentRange.start, { type: DateType.MONTH });
      }
      case QUARTER: {
        const startMonth = dateFormatter(currentRange.start, { type: DateType.MONTH });
        const endMonth = dateFormatter(currentRange.end, { type: DateType.QUARTER });
        return `${startMonth} - ${endMonth}`;
      }
      case YEAR: {
        return dateFormatter(currentRange.start, { type: DateType.YEAR });
      }
      default:
        return '';
    }
  }, [dateFormatter, rangesApi, selectedPeriod.value]);

  const { lineChartData, cumulativeLineChartData, barChartData, cumulativeBarChartData } = usePerformanceData(
    selectedPeriod.value,
    selectedMetric.value,
    daySelected,
    relativeDates,
    periodScope,
    graphNumberFormatter,
    dateFormatter,
  );

  const lineGraphData = cumulativeTotals ? cumulativeLineChartData : lineChartData;
  const barGraphData = cumulativeTotals ? cumulativeBarChartData : barChartData;

  const dragInitialPosition = React.useMemo(() => {
    const { currentRange } = rangesApi;
    switch (selectedPeriod.value) {
      case WEEK:
        return WEEKDAY_NAMES[(currentRange.end.getDay() + 6) % 7];
      case MONTH:
        return currentRange.end.getDate();
      case QUARTER:
        return MONTH_NAMES[currentRange.end.getMonth()];
      case YEAR:
        return MONTH_NAMES[currentRange.end.getMonth()];
      default:
        return currentRange.end.getDate();
    }
  }, [rangesApi, selectedPeriod.value]);

  const onDragChange = React.useCallback(
    value => {
      setDaySelected(prevDate => {
        const year = prevDate.getFullYear();
        const month = prevDate.getMonth();
        switch (selectedPeriod.value) {
          case WEEK: {
            // Calculate Monday of the current week.
            const date = prevDate.getDate();
            const currentDayOfWeek = prevDate.getDay(); // Sunday = 0, Monday = 1, etc.
            const offsetToMonday = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;
            const weekdayIndex = WEEKDAY_NAMES.indexOf(value); // 'value' is one of ['Sun', 'Mon', ..., 'Sat']

            const totalOffset = offsetToMonday + weekdayIndex;
            const selectedDate = new Date(Date.UTC(year, month, date + totalOffset));

            return utcDate(
              `${selectedDate.getUTCFullYear()}-${selectedDate.getUTCMonth() + 1}-${selectedDate.getUTCDate()}`,
            );
          }
          case MONTH: {
            // 'value' is a day number (from 1 to 31).
            // Calculate how many days the current month has
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            // if value exceeds the maximum, we limit it to the last day of the month
            const day = Math.min(value, daysInMonth);

            return utcDate(`${year}-${month + 1}-${day}`);
          }
          case QUARTER:
          case YEAR: {
            // 'value' is one of ['Jan', 'Feb', ..., 'Dec'].
            const monthIndex = MONTH_NAMES.indexOf(value);
            return utcDate(`${year}-${monthIndex + 1}`);
          }
          default:
            return prevDate;
        }
      });
    },
    [selectedPeriod],
  );

  const onPreviousScope = React.useCallback(() => {
    startGraphTransition(() => {
      setPeriodScope(prev => (prev === PERIOD_SCOPE.CURRENT ? PERIOD_SCOPE.PREVIOUS : PERIOD_SCOPE.CURRENT));
    });
  }, [startGraphTransition]);

  const onNextScope = React.useCallback(() => {
    startGraphTransition(() => {
      setPeriodScope(prev => (prev === PERIOD_SCOPE.PREVIOUS ? PERIOD_SCOPE.CURRENT : PERIOD_SCOPE.CURRENT));
    });
  }, [startGraphTransition]);

  return (
    <div className="performance-tab">
      <div className="my-2">
        <PerformanceToolbar
          periodOptions={PERIOD_OPTIONS}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={onPeriodChange}
          metricOptions={METRIC_OPTIONS}
          selectedMetric={selectedMetric}
          setSelectedMetric={onMetricChange}
          cumulativeTotals={cumulativeTotals}
          setCumulativeTotals={onCumulativeToggle}
        />
      </div>
      {isCalculatingResults ? (
        <ProfitRoverSkeleton />
      ) : (
        <ProfitRoverCard className="performance-card">
          <div className="performance-card-content">
            <ScopeControl
              title={formatTitle()}
              currentScope={periodScope}
              onPreviousScope={onPreviousScope}
              onNextScope={onNextScope}
            />
            <div className="graph-container">
              <div className="line-graph-content">
                {isLoadingGraphs ? (
                  <CenteredProfitRoverSpinner />
                ) : (
                  <PerformanceLineChart
                    data={lineGraphData}
                    numberFormatter={graphNumberFormatter}
                    yLabel={cumulativeTotals ? `Cumulative ${selectedMetric.label}` : selectedMetric.label}
                    xLabel={GRAPH_TIME_GRANULARITY_LABELS[selectedPeriod.value]}
                    onDragChange={onDragChange}
                    dragInitialPosition={dragInitialPosition}
                  />
                )}
              </div>
              <div className="vertical-divider" />
              <div className="bar-graph-content">
                {isLoadingGraphs ? (
                  <CenteredProfitRoverSpinner />
                ) : (
                  <>
                    <h4 className="title">
                      {formatBarGraphTitle(selectedPeriod, daySelected, cumulativeTotals, locationTimezone, locale)}
                    </h4>
                    <BreakdownSection
                      selectedPeriod={selectedPeriod}
                      selectedMetric={selectedMetric}
                      cumulativeTotals={cumulativeTotals}
                      periodScope={periodScope}
                      daySelected={daySelected}
                      graphNumberFormatter={graphNumberFormatter}
                      dateFormatter={dateFormatter}
                      barGraphData={barGraphData}
                      openFiltersMenu={openFiltersMenu}
                    />
                  </>
                )}
              </div>
            </div>
            {!isLoadingGraphs && (
              <div className="legend-container">
                {periodScope === PERIOD_SCOPE.CURRENT && (
                  <div className="legend-item">
                    <span className="legend-dashed-line" />
                    <span className="legend-text">Forecast</span>
                  </div>
                )}
                {lineGraphData.map((series, index) => (
                  <div key={index} className="legend-item">
                    <span className="legend-line" style={{ backgroundColor: PERFORMANCE_LINE_CHART_COLORS[index] }} />
                    <span className="legend-text">{series.legend} Actuals</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ProfitRoverCard>
      )}
    </div>
  );
};

const PerformanceTabContextController = ({ children, isActive }) => {
  const { workflowId } = React.useContext(DomainContext);
  const { isLoadingPerformance, isApplyingFilters, performanceSettingsApi } = React.useContext(DashboardContext);
  const { isLoadingPerformanceSettings } = performanceSettingsApi;

  const { data: performanceMetadata = {}, isLoading: isLoadingPerformanceMeta } = usePerformanceForecastMeta(
    workflowId,
    {
      enabled: isActive,
    },
  );
  const isLoading =
    isLoadingPerformance || isLoadingPerformanceMeta || isApplyingFilters || isLoadingPerformanceSettings;

  const contextValue = {
    performanceMetadata,
  };

  return (
    <PerformanceTabContext.Provider value={contextValue}>
      {isLoading ? <ProfitRoverSkeleton /> : children}
    </PerformanceTabContext.Provider>
  );
};

const PerformanceTabContainer = ({ isActive, openFiltersMenu }) => {
  return (
    <PerformanceTabContextController isActive={isActive}>
      <PerformanceTab openFiltersMenu={openFiltersMenu} />
    </PerformanceTabContextController>
  );
};

export default PerformanceTabContainer;
